import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
    VideoLoadingPlaceholder,
    SlowLoadingHint,
    PreloadErrorContainer,
    PreloadErrorMessage,
    PreloadErrorButton,
} from './CultureFitModule.style';
import { getCultureFitVideoId } from 'utils/module-identificators';

import VideoComponent from 'components/shared/VideoComponent/VideoComponent';
import CultureFitIntroduction from './CultureFitIntroduction/CultureFitIntroduction';
import Logger from 'services/Logger';

import { RotateSpinLoader } from 'react-css-loaders';
const videoId = getCultureFitVideoId();

// Hint timing tiers — lets users on slow connections know we haven't given
// up, with progressively stronger reassurance the longer the wait gets.
//   0–10s : silent spinner (most users finish in this window)
//   10s+  : "Loading, please wait" (gentle reassurance)
//   30s+  : "Your internet connection seems slow..." (clearer signal)
//   120s+ : block with error UI (PRELOAD_FALLBACK_TIMEOUT_MS)
const LOADING_HINT_TIMEOUT_MS = 10000;
const SLOW_LOAD_HINT_TIMEOUT_MS = 30000;

// Safety-net cap matching the saga's video-preload XHR timeout. If the
// preloader entry never settles (e.g., the preload action was never even
// dispatched because of an upstream throw), we still want the user to see
// a clear "network too slow" error rather than a permanent spinner.
const PRELOAD_FALLBACK_TIMEOUT_MS = 120000;

export class CultureFitModule extends Component {
    constructor(props) {
        super(props);

        this.state = {
            introductionAccepted: false,
            preloadFailed: false,
            showLoadingHint: false,
            slowLoading: false,
        };

        this.acceptIntroduction = this.acceptIntroduction.bind(this);
        this.getPreloadedVideo = this.getPreloadedVideo.bind(this);
    }

    getPreloadedVideo() {
        if (this.state.videoUrl) return; // already resolved
        if (this.state.preloadFailed) return; // already in error state

        const { preloader } = this.props;
        const entry = preloader && videoId && preloader[videoId];

        // Preloader succeeded — use the buffered blob URL. The fallbackUrl
        // (the original streaming URL) is kept as a secondary src so
        // VideoComponent's onError recovery chain can swap to it if the
        // platform rejects the blob URL itself (older Safari quirks).
        if (entry && entry.ready) {
            this.setState({
                videoUrl: entry.url,
                fallbackVideoUrl: entry.fallbackUrl,
            });
            return;
        }

        // Preloader settled but failed (loading=false, ready=false). Don't
        // fall back to streaming the file during playback — that would
        // re-buffer mid-video and the resulting recording would capture the
        // user staring at a stuttering player instead of actually engaging
        // with the content. Show a clear error instead.
        if (entry && entry.loading === false && !entry.ready) {
            Logger.logWarning(
                'CultureFitModule - video preload failed, blocking scan',
                {
                    videoId,
                    error:
                        entry.error && entry.error.message
                            ? entry.error.message
                            : entry.error,
                },
            );
            this.setState({ preloadFailed: true });
        }
    }

    componentDidMount() {
        this.getPreloadedVideo();

        // First reassurance hint — gentle "loading" message after a short
        // wait. Don't block — the saga still has time to succeed.
        this.loadingHintTimeout = setTimeout(() => {
            if (this.state.videoUrl) return;
            if (this.state.preloadFailed) return;
            this.setState({ showLoadingHint: true });
        }, LOADING_HINT_TIMEOUT_MS);

        // Stronger hint — "your connection is slow" after a longer wait.
        // Still doesn't block; the saga has up to 120s to succeed.
        this.slowLoadHintTimeout = setTimeout(() => {
            if (this.state.videoUrl) return;
            if (this.state.preloadFailed) return;
            this.setState({ slowLoading: true });
        }, SLOW_LOAD_HINT_TIMEOUT_MS);

        // Safety net: only fires when no preloader entry exists at all
        // (e.g., upstream threw before dispatching the preload action) so
        // the user isn't left on a spinner forever. If the saga IS in
        // progress (entry.loading=true), we yield to it — the saga's own
        // 120s XHR timeout will dispatch FAILURE and componentDidUpdate
        // picks it up. Cutting the saga off prematurely would block
        // borderline-slow networks where the preload would have succeeded.
        this.preloadFallbackTimeout = setTimeout(() => {
            if (this.state.videoUrl) return;
            if (this.state.preloadFailed) return;
            const { preloader } = this.props;
            const entry = preloader && preloader[videoId];
            if (entry) return; // saga is handling it — let it finish
            Logger.logWarning(
                'CultureFitModule - no preloader entry after timeout, blocking scan',
                { videoId },
            );
            this.setState({ preloadFailed: true });
        }, PRELOAD_FALLBACK_TIMEOUT_MS);
    }

    componentDidUpdate() {
        this.getPreloadedVideo();
    }

    componentWillUnmount() {
        if (this.loadingHintTimeout) {
            clearTimeout(this.loadingHintTimeout);
        }
        if (this.slowLoadHintTimeout) {
            clearTimeout(this.slowLoadHintTimeout);
        }
        if (this.preloadFallbackTimeout) {
            clearTimeout(this.preloadFallbackTimeout);
        }
    }

    acceptIntroduction() {
        this.setState({
            introductionAccepted: true,
        });
    }

    render() {
        const {
            introductionAccepted,
            videoUrl,
            fallbackVideoUrl,
            preloadFailed,
            showLoadingHint,
            slowLoading,
        } = this.state;
        const { t } = this.props;

        // Preload failed → show clear error and let the user retry by
        // refreshing on a better connection. Recording during a stuttering
        // streaming playback would invalidate the assessment data, so we
        // block the scan rather than degrade silently.
        if (preloadFailed) {
            return (
                <PreloadErrorContainer>
                    <PreloadErrorMessage>
                        {t('CULTURE_FIT_VIDEO_LOAD.ERROR_MESSAGE')}
                    </PreloadErrorMessage>
                    <PreloadErrorButton
                        onClick={() => window.location.reload()}>
                        {t('CULTURE_FIT_VIDEO_LOAD.REFRESH_BUTTON')}
                    </PreloadErrorButton>
                </PreloadErrorContainer>
            );
        }

        return (
            <React.Fragment>
                {!introductionAccepted && (
                    <CultureFitIntroduction
                        isOptOut={this.props.isOptOut}
                        onNext={() => this.acceptIntroduction()}
                    />
                )}
                {introductionAccepted && videoUrl && (
                    <VideoComponent
                        videoId={videoId}
                        videoUrl={videoUrl}
                        fallbackVideoUrl={fallbackVideoUrl}
                        playAutomatically={true}
                        onStartPlaying={this.props.onStartRecording}
                        onComplete={this.props.onComplete}
                        isVideoFullscreen={true}
                    />
                )}
                {introductionAccepted && !videoUrl && (
                    <VideoLoadingPlaceholder visible={!videoUrl}>
                        <RotateSpinLoader color="#fff" size="2" />
                        {slowLoading ? (
                            <SlowLoadingHint>
                                {t('CULTURE_FIT_VIDEO_LOAD.SLOW_HINT')}
                            </SlowLoadingHint>
                        ) : showLoadingHint ? (
                            <SlowLoadingHint>
                                {t('CULTURE_FIT_VIDEO_LOAD.LOADING_HINT')}
                            </SlowLoadingHint>
                        ) : null}
                    </VideoLoadingPlaceholder>
                )}
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state) => ({
    preloader: state.preloader.videos,
});

export default connect(mapStateToProps)(withTranslation()(CultureFitModule));
